import * as XLSX from 'xlsx';

/** Cabeceras recomendadas en plantilla (alineadas al formulario “Nuevo usuario” en lidz_front). */
export const BULK_USER_TEMPLATE_HEADERS = [
  'nombre',
  'email',
  'telefono',
  'id_externo',
  'puede_asignarse_clientes',
] as const;

export interface ParsedBulkUserRow {
  id: number;
  nombre: string;
  email: string;
  telefono: string;
  idExterno: string;
  puedeAsignarseClientes: string;
}

function normalizeHeaderKey(h: unknown): string {
  if (typeof h !== 'string' && typeof h !== 'number') return '';
  return String(h)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');
}

function isEmptyRow(values: unknown[]): boolean {
  return values.every(
    (value) =>
      value === null || value === undefined || value === '' || (typeof value === 'string' && value.trim() === '')
  );
}

function pickCell(row: Record<string, unknown>, keys: string[]): unknown {
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    const v = row[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') return v;
  }
  return '';
}

function cellString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return String(value).replace(/\s+/g, ' ').trim();
}

/**
 * Lee la primera hoja. Obligatorias: nombre, email.
 * Opcionales: telefono, id_externo,
 * puede_asignarse_clientes (si/no, true/false, 1/0).
 */
export function parseUsersBulkFile(file: File): Promise<ParsedBulkUserRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          reject(new Error('No se pudo leer el archivo'));
          return;
        }
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonDataRaw: unknown[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });

        if (!jsonDataRaw || jsonDataRaw.length === 0) {
          reject(new Error('El archivo no contiene datos'));
          return;
        }

        const headerRow = jsonDataRaw[0];
        if (!Array.isArray(headerRow) || headerRow.length === 0) {
          reject(new Error('Faltan encabezados en el archivo'));
          return;
        }

        const headers = headerRow.map((cell) => normalizeHeaderKey(cell));
        const headerSet = new Set(headers.filter(Boolean));

        const hasNombre = headerSet.has('nombre') || headerSet.has('name');
        const hasEmail = headerSet.has('email') || headerSet.has('correo');
        if (!hasNombre || !hasEmail) {
          reject(new Error('El archivo debe incluir columnas obligatorias "nombre" y "email"'));
          return;
        }

        const dataRows = jsonDataRaw.slice(1).filter((row): row is unknown[] => {
          if (!Array.isArray(row)) return false;
          const keyed: Record<string, unknown> = {};
          for (let hi = 0; hi < headers.length; hi += 1) {
            const h = headers[hi];
            if (h) keyed[h] = row[hi];
          }
          const nombreVal = pickCell(keyed, ['nombre', 'name']);
          const emailVal = pickCell(keyed, ['email', 'correo']);
          return !isEmptyRow([nombreVal, emailVal]);
        });

        const rows: ParsedBulkUserRow[] = dataRows.map((row, index) => {
          const keyed: Record<string, unknown> = {};
          for (let hi = 0; hi < headers.length; hi += 1) {
            const h = headers[hi];
            if (h) keyed[h] = row[hi];
          }
          const nombre = cellString(pickCell(keyed, ['nombre', 'name']));
          const email = cellString(pickCell(keyed, ['email', 'correo'])).toLowerCase();
          const telefono = cellString(pickCell(keyed, ['telefono', 'phone', 'phonenumber']));
          const idExterno = cellString(pickCell(keyed, ['id_externo', 'externalid', 'external_id', 'idexterno']));
          const puedeAsignarseClientes = cellString(
            pickCell(keyed, [
              'puede_asignarse_clientes',
              'asignable',
              'isassignable',
              'puede_ser_asignado',
              'puede_ser_asignado_a_clientes',
            ])
          );
          return {
            id: index + 1,
            nombre,
            email,
            telefono,
            idExterno,
            puedeAsignarseClientes,
          };
        });

        resolve(rows);
      } catch (err) {
        reject(err instanceof Error ? err : new Error('Error al procesar el archivo'));
      }
    };
    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsArrayBuffer(file);
  });
}

export function downloadUsersBulkTemplate(): void {
  const sheet = XLSX.utils.aoa_to_sheet([
    [...BULK_USER_TEMPLATE_HEADERS],
    ['María González', 'maria.gonzalez@ejemplo.com', '+56911112222', 'EXT-001', 'si'],
    ['Carlos Pérez', 'carlos.perez@ejemplo.com', '+56922223333', 'EXT-002', 'no'],
    ['Ana Martínez', 'ana.martinez@ejemplo.com', '', '', 'true'],
    ['Luis Fernández', 'luis.fernandez@ejemplo.com', '56933334444', 'CRM-8841', '1'],
    ['Patricia Soto', 'patricia.soto@ejemplo.com', '+56944445555', 'EXT-005', '0'],
    ['Diego Rojas', 'diego.rojas@ejemplo.com', '', 'SIN-CRM', 'si'],
  ]);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Usuarios');
  XLSX.writeFile(book, 'plantilla_carga_usuarios_lidz.xlsx');
}
